import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Mail, Phone, Calendar, User, Briefcase, DollarSign, MessageSquare } from "lucide-react";

type ContactMessage = {
  id: string;
  created_at: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  service: string;
  budget: string | null;
  message: string;
  status: string;
  priority: string;
};

const PRIORITY_LABELS: Record<string, string> = {
  low: "Faible",
  normal: "Normale",
  high: "Haute",
  urgent: "Urgente",
};

const PRIORITY_CLASSES: Record<string, string> = {
  low: "bg-slate-200 text-slate-700 hover:bg-slate-200 border-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:border-slate-600",
  normal: "bg-sky-100 text-sky-800 hover:bg-sky-100 border-sky-300 dark:bg-sky-900 dark:text-sky-100 dark:border-sky-700",
  high: "bg-amber-100 text-amber-900 hover:bg-amber-100 border-amber-400 dark:bg-amber-900 dark:text-amber-100 dark:border-amber-700",
  urgent: "bg-red-600 text-white hover:bg-red-600 border-red-700 animate-pulse",
};


const AdminContactMessages = () => {
  const { toast } = useToast();
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);

  const { data: messages, isLoading, refetch } = useQuery({
    queryKey: ["contact-messages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contact_messages")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as ContactMessage[];
    },
  });

  const handleStatusChange = async (id: string, newStatus: string) => {
    const { error } = await supabase
      .from("contact_messages")
      .update({ status: newStatus })
      .eq("id", id);

    if (error) {
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le statut",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Statut mis à jour",
      description: "Le statut du message a été modifié avec succès",
    });
    refetch();
  };

  const handlePriorityChange = async (id: string, newPriority: string) => {
    const { error } = await supabase
      .from("contact_messages")
      .update({ priority: newPriority })
      .eq("id", id);

    if (error) {
      toast({ title: "Erreur", description: "Impossible de mettre à jour la priorité", variant: "destructive" });
      return;
    }
    toast({ title: "Priorité mise à jour" });
    refetch();
  };

  const getPriorityBadge = (priority: string) => (
    <Badge variant="outline" className={`font-semibold ${PRIORITY_CLASSES[priority] || ""}`}>
      {PRIORITY_LABELS[priority] || priority}
    </Badge>
  );


  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      new: "destructive",
      in_progress: "default",
      resolved: "secondary",
    };

    const labels: Record<string, string> = {
      new: "Nouveau",
      in_progress: "En cours",
      resolved: "Résolu",
    };

    return (
      <Badge variant={variants[status] || "outline"}>
        {labels[status] || status}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Messages de contact - Admin</title>
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Messages de contact</CardTitle>
            <CardDescription>
              Gérez tous les messages reçus via le formulaire de contact. Cliquez sur une ligne pour voir les détails.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Nom</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Téléphone</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Budget</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Priorité</TableHead>
                    <TableHead>Statut</TableHead>

                  </TableRow>
                </TableHeader>
                <TableBody>
                  {messages && messages.length > 0 ? (
                    messages.map((message) => (
                      <TableRow
                        key={message.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => setSelectedMessage(message)}
                      >
                        <TableCell className="whitespace-nowrap">
                          {format(new Date(message.created_at), "d MMM yyyy HH:mm", {
                            locale: fr,
                          })}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {message.first_name} {message.last_name}
                        </TableCell>
                        <TableCell>
                          <span className="text-accent">{message.email}</span>
                        </TableCell>
                        <TableCell>
                          {message.phone || <span className="text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell>{message.service}</TableCell>
                        <TableCell>
                          {message.budget || <span className="text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell className="max-w-xs">
                          <p className="line-clamp-2 text-sm">{message.message}</p>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Select
                            value={message.priority || 'normal'}
                            onValueChange={(value) => handlePriorityChange(message.id, value)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue>{getPriorityBadge(message.priority || 'normal')}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">Faible</SelectItem>
                              <SelectItem value="normal">Normale</SelectItem>
                              <SelectItem value="high">Haute</SelectItem>
                              <SelectItem value="urgent">Urgente</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>

                          <Select
                            value={message.status}
                            onValueChange={(value) => handleStatusChange(message.id, value)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue>
                                {getStatusBadge(message.status)}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">Nouveau</SelectItem>
                              <SelectItem value="in_progress">En cours</SelectItem>
                              <SelectItem value="resolved">Résolu</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-muted-foreground">
                        Aucun message pour le moment
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dialog de détail du message */}
      <Dialog open={!!selectedMessage} onOpenChange={(open) => !open && setSelectedMessage(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Détail du message
            </DialogTitle>
          </DialogHeader>

          {selectedMessage && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  {format(new Date(selectedMessage.created_at), "d MMMM yyyy à HH:mm", { locale: fr })}
                </div>
                <div className="flex items-center gap-2">
                  {getPriorityBadge(selectedMessage.priority || 'normal')}
                  {getStatusBadge(selectedMessage.status)}
                </div>

              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                    <User className="h-3 w-3" /> Nom
                  </p>
                  <p className="text-sm font-semibold">{selectedMessage.first_name} {selectedMessage.last_name}</p>
                </div>

                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                    <Mail className="h-3 w-3" /> Courriel
                  </p>
                  <a href={`mailto:${selectedMessage.email}`} className="text-sm text-accent hover:underline">
                    {selectedMessage.email}
                  </a>
                </div>

                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                    <Phone className="h-3 w-3" /> Téléphone
                  </p>
                  {selectedMessage.phone ? (
                    <a href={`tel:${selectedMessage.phone}`} className="text-sm text-accent hover:underline">
                      {selectedMessage.phone}
                    </a>
                  ) : (
                    <p className="text-sm text-muted-foreground">Non fourni</p>
                  )}
                </div>

                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                    <Briefcase className="h-3 w-3" /> Service
                  </p>
                  <p className="text-sm">{selectedMessage.service}</p>
                </div>

                {selectedMessage.budget && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                      <DollarSign className="h-3 w-3" /> Budget
                    </p>
                    <p className="text-sm">{selectedMessage.budget}</p>
                  </div>
                )}
              </div>

              <div className="border-t pt-4 space-y-1">
                <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                  <MessageSquare className="h-3 w-3" /> Message
                </p>
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{selectedMessage.message}</p>
              </div>

              <div className="border-t pt-4 flex flex-wrap items-center gap-3">

                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Priorité :</span>
                  <Select
                    value={selectedMessage.priority || 'normal'}
                    onValueChange={(value) => {
                      handlePriorityChange(selectedMessage.id, value);
                      setSelectedMessage({ ...selectedMessage, priority: value });
                    }}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue>{getPriorityBadge(selectedMessage.priority || 'normal')}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Faible</SelectItem>
                      <SelectItem value="normal">Normale</SelectItem>
                      <SelectItem value="high">Haute</SelectItem>
                      <SelectItem value="urgent">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Statut :</span>
                  <Select
                    value={selectedMessage.status}
                    onValueChange={(value) => {
                      handleStatusChange(selectedMessage.id, value);
                      setSelectedMessage({ ...selectedMessage, status: value });
                    }}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue>{getStatusBadge(selectedMessage.status)}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">Nouveau</SelectItem>
                      <SelectItem value="in_progress">En cours</SelectItem>
                      <SelectItem value="resolved">Résolu</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </DialogContent>

      </Dialog>
    </>
  );
};

export default AdminContactMessages;
